import type { ThemeMode } from '../stores/useSettingsStore'

/**
 * The theme setting this device last showed. It themes everything that comes
 * before a session's own settings: the startup loading screen (index.html
 * reads this key before the app loads), signed-out pages, and the gap while
 * the app switches identities. Without one, the browser's setting applies.
 * Signed-in pages still wait for the session's authoritative theme.
 */
export const DEVICE_THEME_KEY = 'arvoke.deviceTheme.v1'

export function readDeviceTheme(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(DEVICE_THEME_KEY)
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function writeDeviceTheme(setting: ThemeMode) {
  try {
    window.localStorage.setItem(DEVICE_THEME_KEY, setting)
  } catch {
    /* Storage may be unavailable; the browser's setting applies next time. */
  }
}

function resolveTheme(setting: ThemeMode): 'light' | 'dark' {
  if (setting !== 'system') return setting
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** The theme before any session's settings, as index.html applies it. */
export function applyDeviceTheme() {
  const root = document.documentElement
  const resolved = resolveTheme(readDeviceTheme())
  delete root.dataset.themeSetting
  delete root.dataset.themeResolved
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
}
